import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../Components/Loading/Loading";
import Util from "../../Components/ShowCard/util";
import { fetchActivity } from "./service";
import { Link, useNavigate } from "react-router-dom";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { ReactComponent as ChevronIcon } from "../../assets/Icons/chevron-down.svg";
import CalendarPopup from "../Calendar/components/CalendarPopup";
import AddToPlaylistPopup from "../ShowDetailsScreen/Components/AddToPlaylistPopup";
import Translate from "../../Components/Multilanguage/Translate";
import { getUserSubscription } from "../../network/service";
import { getUserSubscriptionData } from "../../Redux/UserSubscriptionDetails/userSubscriptionSlice";

const MyActivity = () => {
    const projectInfo = useSelector((state) => state?.projectInfo?.value);
    const accessToken = useSelector((state) => state?.accessToken?.value);
    const user = useSelector((state) => state?.user?.value);
    // const userSubscriptionData = useSelector((state) => state?.userSubscription?.value);
    const dispatch = useDispatch();

    const getDefaultDateRange = () => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 29);
        return [thirtyDaysAgo, today];
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [dateRange, setDateRange] = useState(getDefaultDateRange());
    const [completionFilter, setCompletionFilter] = useState('All');
    const [showCalendar, setShowCalendar] = useState(false);
    const [showCompletionDropdown, setShowCompletionDropdown] = useState(false);
    const [showCalendarPopup, setShowCalendarPopup] = useState(false);
    const [showAddtoPlaylistPopup, setShowAddtoPlaylistPopup] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);

    const calendarRef = useRef(null);
    const completionRef = useRef(null);
    const navigate = useNavigate();

    const appInfo = {
        projectDetails: projectInfo,
        accessToken: accessToken,
    };

    const updateVideoWatchlistStatus = (videoId, newStatus) => {
        const watchlistFlag = newStatus === "added" ? 1 : 0;
        
        setData(prevData => 
            prevData.map(dateGroup => ({
                ...dateGroup,
                videos: dateGroup.videos.map(video => 
                    video.video_id === videoId 
                        ? { ...video, watchlist_flag: watchlistFlag }
                        : video
                )
            }))
        );

        setFilteredData(prevFilteredData => 
            prevFilteredData.map(dateGroup => ({
                ...dateGroup,
                videos: dateGroup.videos.map(video => 
                    video.video_id === videoId 
                        ? { ...video, watchlist_flag: watchlistFlag }
                        : video
                )
            }))
        );
    };

    const fetchUserSubscriptionDetails = async () => {
        try {
            const userSubResponse = await getUserSubscription(appInfo);
            if (userSubResponse?.status === 200) {
                dispatch(getUserSubscriptionData({
                    userSubscription: userSubResponse?.data,
                }));
                const hasActiveSubscription = userSubResponse?.data?.data?.length > 0;
                setHasSubscription(hasActiveSubscription);
                return hasActiveSubscription;
            }
            setHasSubscription(false);
            return false;
        } catch (err) {
            setHasSubscription(false);
            return false;
        }
    };

    useEffect(() => {
        window.scroll(0, 0);
    }, []);

    useEffect(() => {
        const initializePage = async () => {
            if (!user) {
                setLoading(false);
                return;
            }

            const hasActiveSubscription = await fetchUserSubscriptionDetails();
            
            if (hasActiveSubscription) {
                await fetchUserActivity();
            } else {
                setLoading(false);
            }
        };

        initializePage();
    }, [user]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
            if (completionRef.current && !completionRef.current.contains(event.target)) {
                setShowCompletionDropdown(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setShowCalendar(false);
                setShowCompletionDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, []);

    const fetchUserActivity = async (startDate = null, endDate = null) => {
        if (!user) {
            setLoading(false);
            return;
        }
        try {
            const params = {};
            if (startDate && endDate) {
                params.start_date = formatDateForApi(startDate);
                params.end_date = formatDateForApi(endDate);
            }
            const response = await fetchActivity(appInfo, params);
            if (response?.status === 200) {
                const responseData = response?.data?.data || [];
                setData(responseData);
                applyCompletionFilter(responseData, completionFilter);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    const formatDateForApi = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const applyCompletionFilter = (data, filter) => {
        let filtered = data;
        if (filter === 'Completed') {
            filtered = data.map(dateGroup => ({
                ...dateGroup,
                videos: dateGroup.videos.filter(video => video.completed === true)
            })).filter(dateGroup => dateGroup.videos.length > 0);
        } else if (filter === 'Incomplete') {
            filtered = data.map(dateGroup => ({
                ...dateGroup,
                videos: dateGroup.videos.filter(video => video.completed === false)
            })).filter(dateGroup => dateGroup.videos.length > 0);
        }
        setFilteredData(filtered);
    };

    useEffect(() => {
        if (hasSubscription) {
            fetchUserActivity(dateRange[0], dateRange[1]);
        }
    }, [dateRange]);

    useEffect(() => {
        applyCompletionFilter(data, completionFilter);
    }, [data, completionFilter]);

    const handleDateRangeChange = (newDateRange) => {
        setDateRange(newDateRange);
        setShowCalendar(false);
    };

    const handleCompletionFilterChange = (filter) => {
        setCompletionFilter(filter);
        setShowCompletionDropdown(false);
    };

    const { replaceImage } = Util();

    const formatDateRange = (startDate, endDate) => {
        const options = { month: 'short', day: 'numeric' };
        const start = startDate.toLocaleDateString(undefined, options);
        const end = endDate.toLocaleDateString(undefined, options);
        if (startDate.getFullYear() !== endDate.getFullYear()) {
            return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
        }
        return `${start} - ${end}, ${endDate.getFullYear()}`;
    };

    if (loading) return <Loading />;

    if (!user) {
        return (
            <div className="myActivity">
                <div className="wrapper">
                    <div className="top">
                        <h1 className={`heading`}>My Activity</h1>
                    </div>
                    <div className="bottom">
                        <div className="emptyState">
                            <p>You need to sign in to view your activity.</p>
                            <Link to="/login">Sign in</Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (user && !hasSubscription && !loading) {
        return (
            <div className="myActivity">
                <div className="wrapper">
                    <div className="top">
                        <h1 className={`heading`}>My Activity</h1>
                    </div>
                    <div className="bottom">
                        <div className="emptyState">
                            <p>Only available to Subscribers.</p>
                            <div className="buttonContainer" onClick={() => navigate("/checkout")}>
                                <div className="background"></div>
                                <button>Subscribe Now</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="myActivity">
            <div className="wrapper">
                <div className="top">
                    <div className="headerRow">
                        <h1 className="heading">My Activity</h1>
                        <div className="filters-container">
                            <div className="filters-content">
                                <div className="filters-grid">
                                    <div className="custom-dropdown" ref={calendarRef}>
                                        <div
                                            className={`dropdown-selector ${showCalendar ? 'active' : ''}`}
                                            onClick={() => setShowCalendar(!showCalendar)}
                                            aria-expanded={showCalendar}
                                            aria-haspopup="true"
                                        >
                                            <span className="selected-value">{formatDateRange(dateRange[0], dateRange[1])}</span>
                                            <ChevronIcon className={`chevron-icon ${showCalendar ? 'open' : ''}`} />
                                        </div>
                                        {showCalendar && (
                                            <div className="calendarDropdown">
                                                <div className="calendarHeader">
                                                    <span className="calendarTitle">Select Date Range</span>
                                                    <button
                                                        className="closeButton"
                                                        onClick={() => setShowCalendar(false)}
                                                        aria-label="Close calendar"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                                <Calendar
                                                    onChange={handleDateRangeChange}
                                                    value={dateRange}
                                                    selectRange={true}
                                                    maxDate={new Date()}
                                                    showNeighboringMonth={false}
                                                    prev2Label={null}
                                                    next2Label={null}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="custom-dropdown" ref={completionRef}>
                                        <div
                                            className={`dropdown-selector ${showCompletionDropdown ? 'active' : ''}`}
                                            onClick={() => setShowCompletionDropdown(!showCompletionDropdown)}
                                            aria-expanded={showCompletionDropdown}
                                            aria-haspopup="true"
                                        >
                                            <span className="selected-value">{completionFilter}</span>
                                            <ChevronIcon className={`chevron-icon ${showCompletionDropdown ? 'open' : ''}`} />
                                        </div>
                                        {showCompletionDropdown && (
                                            <div className="dropdown-menu">
                                                <div
                                                    className={`dropdown-item ${completionFilter === 'All' ? 'selected' : ''}`}
                                                    onClick={() => handleCompletionFilterChange('All')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleCompletionFilterChange('All')}
                                                >
                                                    All
                                                </div>
                                                <div
                                                    className={`dropdown-item ${completionFilter === 'Completed' ? 'selected' : ''}`}
                                                    onClick={() => handleCompletionFilterChange('Completed')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleCompletionFilterChange('Completed')}
                                                >
                                                    Completed
                                                </div>
                                                {/* <div
                                                    className={`dropdown-item ${completionFilter === 'Incomplete' ? 'selected' : ''}`}
                                                    onClick={() => handleCompletionFilterChange('Incomplete')}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleCompletionFilterChange('Incomplete')}
                                                >
                                                    Incomplete
                                                </div> */}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bottom">
                    <div className="itemsContainer activityList">
                        {filteredData.map((dateGroup, groupIndex) => (
                            <div className="dateGroup" key={groupIndex}>
                                <h2 className="dateHeading">{dateGroup.date_heading}</h2>
                                <div className="dateItems">
                                    {dateGroup.videos.map((video, videoIndex) => (
                                        <div key={videoIndex} className="activityCard">
                                            <div className="thumb" onClick={() => navigate(`/videos/${video?.video_vanity_url}`)}>
                                                <img
                                                    src={video?.thumbnail || (process.env.REACT_APP_IMAGE_URL + video?.thumbnail_350_200)}
                                                    alt={video?.title}
                                                    onError={(e) => replaceImage(e, "LANDSCAPE", projectInfo)}
                                                />
                                                <div className="playOverlay" aria-hidden="true">
                                                    <span className="playButton" />
                                                </div>
                                                {video?.completed && (
                                                    <span className="watchedBadge">✓</span>
                                                )}
                                                {video?.watched_percentage > 0 && !video?.completed && (
                                                    <div className="progressOverlay">
                                                        <div className="progressBar">
                                                            <div className="progress" style={{ width: `${video?.watched_percentage || 0}%` }} aria-label={`Progress: ${video?.watched_percentage || 0}%`} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="info">
                                                <div className="titleRow">
                                                    <h3 className="title">{video?.title}</h3>
                                                </div>
                                                <p className="description">{video?.description}</p>
                                                <div className="metaRow">
                                                    <span className="duration">{video?.duration_text}</span>
                                                </div>
                                                <div className="actionButtons">
                                                    <button
                                                        className="add-to-calendar-btn"
                                                        onClick={() => {
                                                            setSelectedVideo(video);
                                                            setShowCalendarPopup(true);
                                                        }}
                                                        aria-label="Add to Calendar"
                                                    >
                                                        <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="calendar-icon">
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M7 1C6.44772 1 6 1.44772 6 2V3H5C3.34315 3 2 4.34315 2 6V20C2 21.6569 3.34315 23 5 23H13.101C12.5151 22.4259 12.0297 21.7496 11.6736 21H5C4.44772 21 4 20.5523 4 20V11H20V11.2899C20.7224 11.5049 21.396 11.8334 22 12.2547V6C22 4.34315 20.6569 3 19 3H18V2C18 1.44772 17.5523 1 17 1C16.4477 1 16 1.44772 16 2V3H8V2C8 1.44772 7.55228 1 7 1ZM16 6V5H8V6C8 6.55228 7.55228 7 7 7C6.44772 7 6 6.55228 6 6V5H5C4.44772 5 4 5.44772 4 6V9H20V6C20 5.44772 19.5523 5 19 5H18V6C18 6.55228 17.5523 7 17 7C16.4477 7 16 6.55228 16 6Z" fill="currentColor"></path>
                                                            <path d="M17 16C17 15.4477 17.4477 15 18 15C18.5523 15 19 15.4477 19 16V17H20C20.5523 17 21 17.4477 21 18C21 18.5523 20.5523 19 20 19H19V20C19 20.5523 18.5523 21 18 21C17.4477 21 17 20.5523 17 20V19H16C15.4477 19 15 18.5523 15 18C15 17.4477 15.4477 17 16 17H17V16Z" fill="currentColor"></path>
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M24 18C24 21.3137 21.3137 24 18 24C14.6863 24 12 21.3137 12 18C12 14.6863 14.6863 12 18 12C21.3137 12 24 14.6863 24 18ZM13.9819 18C13.9819 20.2191 15.7809 22.0181 18 22.0181C20.2191 22.0181 22.0181 20.2191 22.0181 18C22.0181 15.7809 20.2191 13.9819 18 13.9819C15.7809 13.9819 13.9819 15.7809 13.9819 18Z" fill="currentColor"></path>
                                                        </svg>
                                                        <Translate textKey={'add_to_calendar'}/>
                                                    </button>
                                                    
                                                    <button
                                                        className="addtoList"
                                                        onClick={() => {
                                                            setSelectedVideo(video);
                                                            setShowAddtoPlaylistPopup(true);
                                                        }}
                                                        aria-label={video.watchlist_flag === 1 ? "Remove from my list" : "Add to playlist"}
                                                    >
                                                        {video.watchlist_flag === 1 ? (
                                                            <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="playlist-icon">
                                                                <path d="M19 13H5v-2h14v2z" fill="currentColor"></path>
                                                            </svg>
                                                        ) : (
                                                            <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="playlist-icon">
                                                                <path d="M11 5h2v14h-2z" fill="currentColor"></path>
                                                                <path d="M5 11h14v2H5z" fill="currentColor"></path>
                                                            </svg>
                                                        )}
                                                        <Translate textKey={video.watchlist_flag === 1 ? 'remove_from_list' : 'add_to_list'} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {filteredData?.length === 0 && (
                            <div className="emptyState">
                                <p>No activity found for the selected criteria.</p>
                                {/* <Link to="/home">Browse videos</Link> */}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <AddToPlaylistPopup
                isOpen={showAddtoPlaylistPopup}
                onClose={() => setShowAddtoPlaylistPopup(false)}
                showDetails={{ show_id: selectedVideo?.show_id }}
                videoId={selectedVideo?.video_id}
                setWatchlistStatus={(status) => updateVideoWatchlistStatus(selectedVideo?.video_id, status)}
            />

            <CalendarPopup
                isOpen={showCalendarPopup}
                onClose={() => setShowCalendarPopup(false)}
                initialDate={new Date()}
                videoId={selectedVideo?.video_id}
                appInfo={appInfo}
            />
        </div>
    );
};

export default MyActivity;
